import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { getConfig } from '../../config/getConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../providers/AppProvider';
import { settings } from '../../config';
import avatarImg_SA from '../../assets/img/initech_ci.png';
import avatarImg from '../../assets/img/team/avatar.png';
import { resetOidcReauthFlag, getAdminInfo } from '../../hooks/CallApi';

const OidcCallback = () => {
  const navigate = useNavigate();
  const { setConfig } = useAppContext();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('로그인 처리 중...');
  const processedRef = useRef(false); // 콜백 중복 실행 방지

  useEffect(() => {
    const handleCallback = async () => {
      try {
        console.info('[OIDC] callback entered:', window.location.href);
        if (processedRef.current) {
          return;
        }
        processedRef.current = true;
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
          console.error('OIDC Error:', error, errorDescription);
          setStatus('error');
          setMessage(`인증 오류: ${errorDescription || error}`);
          setTimeout(() => navigate('/authentication/simple/login'), 3000);
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('인증 코드가 없습니다.');
          setTimeout(() => navigate('/authentication/simple/login'), 3000);
          return;
        }

        setMessage('토큰 교환 중...');

        // PKCE code_verifier 확인 (없으면 재로그인 유도)
        const cv = sessionStorage.getItem('pkce_code_verifier') || localStorage.getItem('pkce_code_verifier');
        if (!cv) {
          setStatus('error');
          setMessage('인증 세션이 만료되었습니다. 다시 로그인해 주세요.');
          // 우리 기본 로그인 화면으로 이동 (reauth 표시)
          setTimeout(() => navigate('/authentication/simple/login?reauth=1'), 1500);
          return;
        }

        // 동일 code 재처리 방지 (StrictMode 재마운트 포함)
        const alreadyProcessedCode = sessionStorage.getItem('processed_oidc_code');
        if (alreadyProcessedCode && alreadyProcessedCode === code) {
          // URL에서 code 제거만 수행하고 종료
          try {
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          } catch (e) { /* no-op */ }
          return;
        }
        // 교환 시작 표시
        sessionStorage.setItem('processed_oidc_code', code);

        // Authorization Code를 Access Token으로 교환
        const oidcTokens = await exchangeCodeForTokens(code);

        if (oidcTokens && oidcTokens.access_token) {
          console.log('OIDC 토큰 획득 성공');

          setMessage('SafeDB 토큰 교환 중...');

          // 교환 전에 OIDC 토큰을 먼저 저장하여, 교환 실패 시 RP-Initiated Logout에 id_token_hint를 사용할 수 있게 함
          try {
            localStorage.setItem('oidc_access_token', oidcTokens.access_token);
            if (oidcTokens.refresh_token) localStorage.setItem('oidc_refresh_token', oidcTokens.refresh_token);
            if (oidcTokens.id_token) localStorage.setItem('oidc_id_token', oidcTokens.id_token);
          } catch (e) { /* no-op */ }

          // OIDC 토큰을 SafeDB JWT로 교환
          const safedbTokens = await exchangeOidcForSafedbToken(oidcTokens.access_token);

          if (safedbTokens && safedbTokens.success && safedbTokens.token) {
            // SafeDB JWT 토큰을 sessionStorage에 저장 (기존 방식 유지)
            sessionStorage.setItem('token', safedbTokens.token);
            sessionStorage.setItem('refreshToken', safedbTokens.refreshToken || '');

            // OIDC 토큰은 이미 저장되었으므로 별도 처리 불필요 (중복 저장 허용)

            // 사용자 정보 저장
            if (safedbTokens.user) {
              sessionStorage.setItem('user', JSON.stringify(safedbTokens.user));
              // OIDC 사용자의 userId를 AppContext에 설정 (사이트 토큰 요청에 필요)
              console.log('OIDC 사용자 정보 수신');

              // OIDC 로그인 시 필요한 config 설정들 (사용자 역할 기반)
              const userRole = safedbTokens.user.userRole || 'ADMIN'; // Auth 서버에서 받은 실제 역할
              const setNav = userRole === 'SA' ? 'SA' : 'ADMIN'; // 역할에 따른 네비게이션 설정
              console.log('OIDC 사용자 역할:', userRole, '→ 네비게이션:', setNav);
              const setting = settings(setNav); // settings 함수로 역할별 설정 가져오기

              // 일반 로그인과 동일한 설정 적용
              setConfig('navvalue', setNav);
              setConfig('userAuth', setting.userAuth);
              setConfig('userId', safedbTokens.user.userId);
              setConfig('userAuth_K', setting.userAuth_K);
              setConfig('userName', safedbTokens.user.userName || safedbTokens.user.userId);
              setConfig('navbarPosition', setting.navbarPosition);
              setConfig('firstLogin', setting.firstLogin);

              // OIDC 사용자도 일반 로그인처럼 실제 사용자 정보 조회
              try {
                console.log('OIDC 사용자 추가 정보 조회 시작');
                const { adminInfo } = await getAdminInfo();

                if (adminInfo && adminInfo.data) {
                  console.log('OIDC 사용자 정보 조회 성공');

                  // 실제 회사 정보 사용 (일반 로그인과 동일)
                  const actualCompanyId = adminInfo.data.userIds?.companyId || 'default';
                  const actualCompanyName = adminInfo.data.companyName || 'Unknown Company';
                  const collapsed = setNav === 'SA' ? true : false;

                  console.log('실제 회사 정보:', {
                    actualCompanyId,
                    actualCompanyName,
                    companyIdLength: actualCompanyId.length,
                    isValidFormat: /^[a-zA-Z0-9]{8}$/.test(actualCompanyId)
                  });

                  setConfig('checkorg', setNav === 'SA' ? '' : actualCompanyName);
                  setConfig('checkorg_companyId', actualCompanyId);
                  setConfig('userUuid', adminInfo.data.userIds?.userUuid);

                  // 아바타 이미지 설정 (실제 이미지 URL 우선)
                  const imgUrl = adminInfo.data.imgUrl && adminInfo.data.imgUrl.trim() !== ''
                    ? adminInfo.data.imgUrl
                    : (setNav === 'SA' ? avatarImg_SA : avatarImg);
                  setConfig('userAvatar', imgUrl);
                  setConfig('isNavbarVerticalCollapsed', collapsed);

                  console.log(`OIDC 최종 설정 완료: ${setNav} → companyId: ${actualCompanyId}, companyName: ${actualCompanyName}`);
                } else {
                  console.warn('OIDC 사용자 정보 조회 실패, Keycloak 정보로 fallback');
                  // Keycloak 정보로 fallback
                  const companyName = safedbTokens.keycloak_info?.company_id || 'Unknown Company';
                  const companyId = safedbTokens.keycloak_info?.company_id || 'default';
                  const collapsed = setNav === 'SA' ? true : false;

                  setConfig('checkorg', setNav === 'SA' ? '' : companyName);
                  setConfig('checkorg_companyId', companyId);
                  setConfig('userAvatar', setNav === 'SA' ? avatarImg_SA : avatarImg);
                  setConfig('isNavbarVerticalCollapsed', collapsed);
                }
              } catch (error) {
                console.error('OIDC 사용자 정보 조회 중 에러:', error);
                // 에러 시에도 Keycloak 정보로 fallback
                const companyName = safedbTokens.keycloak_info?.company_id || 'Unknown Company';
                const companyId = safedbTokens.keycloak_info?.company_id || 'default';
                const collapsed = setNav === 'SA' ? true : false;

                setConfig('checkorg', setNav === 'SA' ? '' : companyName);
                setConfig('checkorg_companyId', companyId);
                setConfig('userAvatar', setNav === 'SA' ? avatarImg_SA : avatarImg);
                setConfig('isNavbarVerticalCollapsed', collapsed);
              }

              console.log(`🔧 OIDC 초기 설정 완료: ${setNav} 사용자 → getAdminInfo 호출하여 실제 정보 적용`);

              // 추가 설정들
              setConfig('isFluid', setting.isFluid);
              setConfig('isRTL', setting.isRTL);
              setConfig('isDark', setting.isDark);
              setConfig('theme', setting.theme);
              setConfig('hiddenMenuActive', setting.hiddenMenuActive);
              setConfig('showBurgerMenu', setting.showBurgerMenu);
              setConfig('navbarStyle', setting.navbarStyle);

              console.log('🔧 OIDC 로그인 config 설정 완료 (settings 함수 사용)');

              // App.js에서 토큰 존재 여부로 로그인 상태를 판단하므로 별도의 상태 세터는 불필요
            }

            console.log('SafeDB JWT 토큰 교환 성공');

            // OIDC 재인증 플래그 리셋 (무한 루프 방지)
            resetOidcReauthFlag();

            setStatus('success');
            setMessage('로그인 성공! 고객사 선택 페이지로 이동합니다...');

            // URL의 code 파라미터 제거 (새로고침 시 재교환 방지)
            try {
              const cleanUrl = window.location.origin + window.location.pathname;
              window.history.replaceState({}, document.title, cleanUrl);
            } catch (e) { /* no-op */ }

            // 사용한 PKCE 제거
            try { sessionStorage.removeItem('pkce_code_verifier'); localStorage.removeItem('pkce_code_verifier'); } catch (e) { /* no-op */ }

            // 항상 기본 화면으로 이동(끊긴 위치 복귀하지 않음)
            // 복원용 임시 데이터 정리
            sessionStorage.removeItem('oidc_return_url');
            sessionStorage.removeItem('oidc_restore_siteToken');
            sessionStorage.removeItem('oidc_restore_checkorg');
            sessionStorage.removeItem('oidc_restore_checkorg_companyId');

            // 사용자 역할에 따른 리다이렉트(기본 화면)
            const userRole = safedbTokens.user.userRole || 'ADMIN';
            const setNav = userRole === 'SA' ? 'SA' : 'ADMIN';
            if (setNav === 'SA') {
              console.info('[OIDC] navigate to /safedb/SA/adminSites');
              navigate('/safedb/SA/adminSites', { replace: true });
            } else {
              console.info('[OIDC] navigate to /safedb/common/key/apikey');
              navigate('/safedb/common/key/apikey', { replace: true });
            }
            console.log('OIDC 로그인 완료 - 역할별 기본 리다이렉트:', setNav);
          } else {
            throw new Error('SafeDB 토큰 교환 실패: ' + (safedbTokens?.message || '알 수 없는 오류'));
          }
        } else {
          throw new Error('OIDC 토큰 교환 실패');
        }

      } catch (error) {
        console.error('Token exchange failed:', error);

        // 에러 발생 시에도 재인증 플래그 리셋
        resetOidcReauthFlag();

        setStatus('error');

        // SA 사용자 차단 에러 처리
        if (error.type === 'SA_LOGIN_BLOCKED') {
          setMessage(error.userMessage || 'SA 권한 사용자는 SSO 로그인이 불가능합니다.');
          // SA 차단 에러는 메시지를 보여준 후 Keycloak 로그아웃 수행 (5초)
          setTimeout(() => {
            try {
              const { SSO_KEYCLOAK_URL, SSO_REALM, SSO_CLIENT_ID } = getConfig();
              const keycloakUrl = SSO_KEYCLOAK_URL || process.env.REACT_APP_SSO_KEYCLOAK_URL;
              const realm = SSO_REALM || process.env.REACT_APP_SSO_REALM;
              const clientId = SSO_CLIENT_ID || process.env.REACT_APP_SSO_CLIENT_ID;
              const idToken = localStorage.getItem('oidc_id_token') || '';
              const redirectUri = `${window.location.origin}/authentication/simple/login`;

              // 로컬 스토리지에서 OIDC 토큰 제거
              localStorage.removeItem('oidc_access_token');
              localStorage.removeItem('oidc_refresh_token');
              localStorage.removeItem('oidc_id_token');

              // Keycloak 로그아웃 URL 생성
              const params = new URLSearchParams({
                post_logout_redirect_uri: redirectUri,
                client_id: clientId
              });
              if (idToken) params.set('id_token_hint', idToken);

              const logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?${params.toString()}`;
              console.log('🔐 SA 사용자 차단 - Keycloak 로그아웃:', logoutUrl);
              window.location.href = logoutUrl;
            } catch (logoutErr) {
              console.error('Keycloak 로그아웃 중 오류:', logoutErr);
              window.location.href = '/authentication/simple/login';
            }
          }, 5000);
        } else if (error.type === 'USER_NOT_FOUND') {
          // 미등록/삭제된 사용자 에러 처리 (401) - 메시지 보여준 후 Keycloak 로그아웃 수행 (5초)
          setMessage(error.userMessage || '등록되지 않은 사용자입니다. 관리자에게 계정 생성을 요청해주세요.');
          setTimeout(() => {
            try {
              const { SSO_KEYCLOAK_URL, SSO_REALM, SSO_CLIENT_ID } = getConfig();
              const keycloakUrl = SSO_KEYCLOAK_URL || process.env.REACT_APP_SSO_KEYCLOAK_URL;
              const realm = SSO_REALM || process.env.REACT_APP_SSO_REALM;
              const clientId = SSO_CLIENT_ID || process.env.REACT_APP_SSO_CLIENT_ID;
              const idToken = localStorage.getItem('oidc_id_token') || '';
              const redirectUri = `${window.location.origin}/authentication/simple/login`;

              // 로컬 스토리지에서 OIDC 토큰 제거
              localStorage.removeItem('oidc_access_token');
              localStorage.removeItem('oidc_refresh_token');
              localStorage.removeItem('oidc_id_token');

              // Keycloak 로그아웃 URL 생성
              const params = new URLSearchParams({
                post_logout_redirect_uri: redirectUri,
                client_id: clientId
              });
              if (idToken) params.set('id_token_hint', idToken);

              const logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?${params.toString()}`;
              console.log('🔐 미등록 사용자 차단 - Keycloak 로그아웃:', logoutUrl);
              window.location.href = logoutUrl;
            } catch (logoutErr) {
              console.error('Keycloak 로그아웃 중 오류:', logoutErr);
              window.location.href = '/authentication/simple/login';
            }
          }, 5000);
        } else {
          setMessage('토큰 교환에 실패했습니다. 다시 시도해주세요.');
          setTimeout(() => navigate('/authentication/simple/login'), 3000);
        }
      }
    };

    handleCallback();
  }, [navigate]);

  // 토큰 교환 함수
  const exchangeCodeForTokens = async (code) => {
    const { SSO_KEYCLOAK_URL: keycloakUrl, SSO_REALM: realm, SSO_CLIENT_ID: clientId } = require('config/getConfig').getConfig();
    const redirectUri = `${window.location.origin}/oidc/callback`;

    const tokenEndpoint = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/token`;

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code: code,
        redirect_uri: redirectUri,
        // PKCE: 로그인 시 생성했던 code_verifier 포함
        code_verifier: sessionStorage.getItem('pkce_code_verifier') || '',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error:', response.status, errorText);
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    return await response.json();
  };

  // OIDC 토큰을 SafeDB JWT로 교환하는 함수
  const exchangeOidcForSafedbToken = async (oidcAccessToken) => {
    const { AUTH_URL } = getConfig();
    const exchangeEndpoint = `${AUTH_URL}/auth/oidc/exchange`;

      console.log('Auth 서버로 OIDC 토큰 교환 요청 :', exchangeEndpoint);

    try {
      // 현재 사이트 토큰 확인 (복원용)
      const currentSiteToken = sessionStorage.getItem('oidc_restore_siteToken');
      const requestBody = {};

      if (currentSiteToken) {
        requestBody.siteToken = currentSiteToken;
        console.log('OidcCallback: Site token context detected for restoration');
      }

      const response = await fetch(exchangeEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${oidcAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      // SA 사용자 차단 에러 처리 (403 FORBIDDEN)
      if (response.status === 403) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: 'SA 권한 사용자는 SSO 로그인이 불가능합니다.' };
        }

        console.warn('🚫 SA 사용자 SSO 로그인 차단:', errorData);

        // SA 차단 에러는 특별히 처리 - 에러 객체에 타입 추가
        const saBlockedError = new Error(errorData.message || 'SA 권한 사용자는 SSO 로그인이 불가능합니다.');
        saBlockedError.type = 'SA_LOGIN_BLOCKED';
        saBlockedError.userMessage = errorData.message || 'SA 권한 사용자는 SSO 로그인이 불가능합니다.\n\n시스템 관리자는 별도의 관리자 로그인을 이용해주세요.';
        throw saBlockedError;
      }

      // 등록되지 않은 사용자 또는 삭제된 사용자 처리 (401 UNAUTHORIZED)
      if (response.status === 401) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: '등록되지 않은 사용자입니다. 관리자에게 계정 생성을 요청해주세요.' };
        }

        console.warn('🚫 미등록 사용자 SSO 로그인 차단:', errorData);

        // 미등록 사용자 에러 처리 - 에러 객체에 타입 추가
        const userNotFoundError = new Error(errorData.message || '등록되지 않은 사용자입니다.');
        userNotFoundError.type = 'USER_NOT_FOUND';
        userNotFoundError.userMessage = errorData.message || '등록되지 않은 사용자입니다. 관리자에게 계정 생성을 요청해주세요.';
        throw userNotFoundError;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('SafeDB 토큰 교환 오류:', response.status, errorText);
        throw new Error(`SafeDB token exchange failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('SafeDB 토큰 교환 응답 수신');

      return result;
    } catch (error) {
      console.error('SafeDB 토큰 교환 중 오류:', error);

      // SA 사용자 차단 에러 또는 미등록 사용자 에러는 로그아웃하지 않고 에러 메시지만 표시
      if (error.type === 'SA_LOGIN_BLOCKED' || error.type === 'USER_NOT_FOUND') {
        throw error; // 상위로 전파하여 UI에서 처리
      }

      // 그 외 교환 실패 시, 보안 상태 보장을 위해 Keycloak RP-Initiated Logout으로 리다이렉트
      try {
        const { SSO_KEYCLOAK_URL, SSO_REALM, SSO_CLIENT_ID } = getConfig();
        const keycloakUrl = SSO_KEYCLOAK_URL || process.env.REACT_APP_SSO_KEYCLOAK_URL || 'http://localhost:8080';
        const realm = SSO_REALM || process.env.REACT_APP_SSO_REALM || 'safedb';
        const clientId = SSO_CLIENT_ID || process.env.REACT_APP_SSO_CLIENT_ID || 'safedb-frontend';
        const idToken = localStorage.getItem('oidc_id_token') || '';
        const redirectUri = `${window.location.origin}/login`;

        const params = new URLSearchParams({
          post_logout_redirect_uri: redirectUri,
          client_id: clientId
        });
        if (idToken) params.set('id_token_hint', idToken);

        const logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?${params.toString()}`;
        console.warn('OIDC 교환 실패로 강제 로그아웃 진행:', logoutUrl);
        window.location.assign(logoutUrl);
        return; // 이후 진행 중단
      } catch (logoutErr) {
        console.error('RP-Initiated Logout 진행 중 오류:', logoutErr);
      }
      throw error;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Spinner animation="border" variant="primary" />;
      case 'success':
        return <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="3x" />;
      case 'error':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" size="3x" />;
      default:
        return <Spinner animation="border" variant="primary" />;
    }
  };

  const getAlertVariant = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'danger';
      default:
        return 'info';
    }
  };

  return (
    <div className="d-flex justify-content-center align-items-center min-vh-100">
      <Card style={{ maxWidth: '450px', width: '100%' }}>
        <Card.Body className="text-center p-5">
          <div className="mb-4">
            {getStatusIcon()}
          </div>

          <h4 className="mb-3">OIDC 인증 처리</h4>

          <Alert variant={getAlertVariant()} className="mb-0">
            {message.split('\n').map((line, index) => (
              <span key={index}>
                {line}
                {index < message.split('\n').length - 1 && <br />}
              </span>
            ))}
          </Alert>

          {status === 'processing' && (
            <div className="mt-3">
              <small className="text-muted">
                잠시만 기다려주세요...
              </small>
            </div>
          )}

          {status === 'error' && (
            <div className="mt-3">
              <small className="text-muted">
                잠시 후 로그인 페이지로 이동합니다...
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default OidcCallback;
